import React, { useState, useEffect, useMemo, useContext } from "react";

import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import ErrorBanner from "../ui/ErrorBanner";
import SkeletonList from "../ui/SkeletonList";
import debounce from 'lodash/debounce';

import { fetchBannerList } from "../../utils/api";
import { useLoading } from "../../context/LoadingContext";
import LanguageContext from "../../context/LanguageContext";
import config from "../../config";
import { getI18nLanguageCode, normalizeLanguageCode } from "../../utils/languageOptions";

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

const getInitialIsDesktop = () => (
    typeof window !== "undefined" && window.matchMedia(DESKTOP_MEDIA_QUERY).matches
);

const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return "";
    if (/^https?:\/\//i.test(mediaPath)) return mediaPath;

    return `${config.APP_URL}${mediaPath}`;
};

const getBannerMediaPath = (banner, isDesktop) => {
    if (isDesktop && banner.translatable_type === "menu" && banner.desktop_content) {
        return banner.desktop_content;
    }
    
    return banner.content;
};

const BannerSlider = () => {
    const { t, i18n } = useTranslation();
    const { setLoading } = useLoading();
    const { languageList } = useContext(LanguageContext);
    const [errorMessage, setErrorMessage] = useState("");

    const [bannerLoading, setBannerLoading] = useState(false);
    const [bannerList, setBannerList] = useState([]);
    const [isDesktop, setIsDesktop] = useState(getInitialIsDesktop);

    const langMap = useMemo(() => {
        const map = {};
        languageList.forEach(lang => {
            const apiCode = normalizeLanguageCode(lang.code);
            const i18nCode = getI18nLanguageCode(apiCode);
            map[i18nCode] = apiCode;
        });
        return map;
    }, [languageList]);

    const filteredBanners = useMemo(() => {
        if (!Array.isArray(bannerList)) return [];

        const currentLang = getI18nLanguageCode(i18n.language);
        const mappedLang = langMap[currentLang] || Object.values(langMap)[0] || "en_us";

        return bannerList.filter(banner => {
            const isCorrectLang = normalizeLanguageCode(banner.language_code) === mappedLang;
            
            const hasContentForPlatform = isDesktop ? !!banner.desktop_content : !!banner.content;

            return isCorrectLang && hasContentForPlatform;
        });
    }, [bannerList, i18n.language, langMap, isDesktop]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setBannerLoading(true);
            setErrorMessage("");
            try {
                const bannerResponse = await fetchBannerList();
                
                setBannerList(bannerResponse.success && Array.isArray(bannerResponse.data) && bannerResponse.data.length > 0
                    ? bannerResponse.data
                    : []);
            } catch (error) {
                setErrorMessage(t("pleaseTryLater"));
            } finally {
                setLoading(false);
                setBannerLoading(false);
            }
        };

        const debouncedLoadData = debounce(() => {
            loadData();
        }, 1000, { leading: true, trailing: false });

        debouncedLoadData();
        
        return () => {
            debouncedLoadData.cancel();
        };
    }, [t, setLoading]);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const handleViewportChange = () => setIsDesktop(mediaQuery.matches);

        handleViewportChange();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handleViewportChange);
            return () => mediaQuery.removeEventListener("change", handleViewportChange);
        }

        mediaQuery.addListener(handleViewportChange);
        return () => mediaQuery.removeListener(handleViewportChange);
    }, []);

    const swiperClassName = isDesktop
        ? "relative w-full swiper-custom"
        : "relative w-full swiper-custom";
    const slideMediaFrameClassName = isDesktop
        ? "relative w-full aspect-[1920/320] flex items-center justify-center bg-[#07131d] overflow-hidden lg:rounded-none rounded-lg"
        : "relative w-full flex items-center justify-center bg-[#07131d] overflow-hidden lg:rounded-none rounded-lg";
    const mediaClassName = isDesktop
        ? "block h-full w-full object-cover"
        : "block h-auto w-full";

    return (
        <>
            <ErrorBanner message={errorMessage} />

            <section className="h-full px-4 bg-secondary lg:h-auto lg:px-0 lg:bg-transparent">
                {bannerLoading ? (
                    <SkeletonList rows={1} height={isDesktop ? 320 : 244} className="rounded-lg" />
                ) : (
                    <Swiper
                        autoHeight
                        spaceBetween={16}
                        slidesPerView={1}
                        pagination={{
                            clickable: true,
                            renderBullet: (index, className) =>
                                `<span class="${className} swiper-bullet"></span>`,
                        }}
                        autoplay={{
                            delay: 3000,
                            disableOnInteraction: false,
                        }}
                        modules={[Pagination, Autoplay]}
                        className={swiperClassName}
                        style={{ paddingBottom: "34px", boxSizing: "border-box" }}
                    >
                        {filteredBanners.map((banner, index) => {
                            const mediaPath = getBannerMediaPath(banner, isDesktop);
                            if (!mediaPath) return null;

                            const fileUrl = getMediaUrl(mediaPath);
                            const isVideo = mediaPath.toLowerCase().endsWith(".mp4");

                            return (
                                <SwiperSlide key={banner.id ?? mediaPath ?? index} className="h-full">
                                    <div className={slideMediaFrameClassName}>
                                        {isVideo ? (
                                            <video
                                                src={fileUrl}
                                                className={mediaClassName}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            />
                                        ) : (
                                            <img
                                                src={fileUrl}
                                                alt={banner.title || ""}
                                                className={mediaClassName}
                                            />
                                        )}
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                )}
            </section>
        </>
    );
};

export default BannerSlider;